"use client";
import { useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  // Запас до вьюпорта, при котором начинаем монтировать (px). Даёт время react-slick
  // собраться до того, как секция реально видна.
  rootMargin?: string;
  // Резервируем высоту, чтобы не было CLS, пока тяжёлый контент не смонтирован.
  minHeight?: number;
  // Якорь секции вешаем на ВНЕШНИЙ (всегда отрендеренный) div, а не внутрь children:
  // иначе до подъезда к вьюпорту children=null → элемента #id нет в DOM → клик по
  // якорю из меню/хироя никуда не скроллит. С id на обёртке скролл к ней срабатывает
  // всегда, а появление в вьюпорте тут же монтирует контент (rootMargin=300px).
  id?: string;
};

// Откладывает МОНТИРОВАНИЕ тяжёлых below-the-fold клиентских секций (react-slick,
// fancybox, react-phone-input, framer-motion) до момента, когда они подъезжают к
// вьюпорту. Их JS не исполняется в окне гидрации хироя → главный поток свободен для
// LCP-паинта. На throttled-мобиле это снимает основной остаток TBT/LCP.
//
// Замечание про SEO: контент этих секций (галереи фото, форма) не является текстом,
// критичным для индексации landing — h1/описание/schema уже в SSR. Поэтому
// отложенный монтаж клиентского контента здесь безопасен для SEO и даёт крупный
// выигрыш в производительности.
export default function DeferHydrate({ children, rootMargin = "300px", minHeight, id }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;

    // Нет IO (очень старый браузер) — показываем сразу, деградация без поломки.
    // setShow в микротаске (а не прямо в теле эффекта) — чтобы не триггерить
    // eslint react-hooks/set-state-in-effect; поведение то же (синхронно на клиенте).
    if (typeof IntersectionObserver === "undefined") {
      queueMicrotask(() => setShow(true));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);

  return (
    <div
      ref={ref}
      id={id}
      // scroll-margin-top: чтобы фиксированная шапка (~60px) не перекрывала верх
      // секции при переходе по якорю.
      className={id ? "scroll-mt-24" : undefined}
      style={minHeight && !show ? { minHeight } : undefined}
    >
      {show ? children : null}
    </div>
  );
}
